"use client";

import React, { useMemo } from "react";
import { FileText } from "lucide-react";

import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useContractHooks } from "@/lib/hooks/api/shared/contracts-graphql";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contracts } from "@/src/gql/graphql";

const CONTRACTS_CONFIG = getEntityTableConfig<Contracts>("contracts");

interface ContactContractsTabProps {
  contactId: string;
  contactName?: string;
  className?: string;
}

/**
 * Contracts sent to this contact. Reuses the universal contracts table
 * (row actions + right-click via the registry's detailActions) filtered by
 * `contact_id`. UniversalEntityPage wraps ColumnManagerProvider →
 * FilterManagerProvider internally (PROVIDER_ORDERING_ENFORCEMENT).
 */
export function ContactContractsTab({
  contactId,
  contactName,
  className = "",
}: ContactContractsTabProps) {
  const contractHooks = useContractHooks();

  const config = useMemo(
    () => ({
      ...CONTRACTS_CONFIG,
      displayName: contactName ? `Contracts for ${contactName}` : "Contracts",
    }),
    [contactName]
  );

  const customFilters = useMemo(() => ({ contact_id: contactId }), [contactId]);

  if (!contactId) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border bg-card p-8 text-center">
        <FileText className="h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-semibold text-card-foreground">No contracts</h3>
        <p className="text-sm text-muted-foreground">
          No contracts have been sent to this contact yet.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <UniversalEntityPage
        entityName="contracts"
        config={config}
        hooks={contractHooks}
        filterPreset="all"
        title={contactName ? `Contracts for ${contactName}` : "Contracts"}
        description="Contracts sent to this contact"
        storageKey={`contact-${contactId}-contracts-column-preferences`}
        customFilters={customFilters}
        showHeader={false}
      />
    </div>
  );
}
